<div class="und">
  <!-- ── Header ── -->
  <div class="und__header">
    <div class="und__header-inner d-flex align-items-center gap-2">
      <div class="und__header-icon-wrap">
        <mat-icon class="und__header-icon">notifications_active</mat-icon>
      </div>
      <div class="flex-grow-1 min-w-0">
        <div class="und__header-title">{{ 'NOTIFICATIONS.TITLE' | translate }}</div>
        <div class="und__header-sub">{{ 'NOTIFICATIONS.SUBTITLE' | translate }}</div>
      </div>
      <span class="und__header-badge" *ngIf="userNotifications.length">{{ userNotifications.length }}</span>
      <button mat-icon-button [tabIndex]="-1" type="button" class="und__close" (click)="dialogRef.close()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- ── Body ── -->
  <div class="und__body">
    <!-- Loader -->
    <div *ngIf="isLoading" class="und__loader d-flex align-items-center justify-content-center" [@fadeInOut]>
      <mat-spinner diameter="32" mode="indeterminate"></mat-spinner>
    </div>

    <!-- Empty state -->
    <div
      *ngIf="!isLoading && !userNotifications.length"
      class="und__empty d-flex flex-column align-items-center justify-content-center gap-2"
    >
      <mat-icon class="und__empty-icon">notifications_none</mat-icon>
      <span class="und__empty-text">No notifications</span>
    </div>

    <!-- Notification list -->
    <div
      class="und__item"
      *ngFor="let item of userNotifications; trackBy: trackNotification"
      [class.und__item--unread]="!item.isRead"
      [ngClass]="getNotificationClass(item.type)"
    >
      <div class="und__item-accent"></div>

      <div class="d-flex align-items-start gap-2 w-100">
        <!-- Icon / avatar -->
        <div class="und__item-icon-wrap flex-shrink-0">
          <app-s3-image
            *ngIf="getItemIcon(item)"
            [isRounded]="true"
            [image]="getItemIcon(item)"
            [avatarName]="getItemUserName(item)"
            class="und__item-avatar"
          ></app-s3-image>
          <mat-icon *ngIf="!getItemIcon(item)" class="und__item-icon">{{ getNotificationIcon(item.type) }}</mat-icon>
        </div>

        <!-- Content -->
        <div class="und__item-content flex-grow-1 min-w-0" [ngSwitch]="item.type">
          <!-- INFO -->
          <ng-container *ngSwitchCase="'INFO'">
            <ng-container
              *ngTemplateOutlet="
                contentBlock;
                context: {
                  title: item.title,
                  desc: item.description,
                  item: item
                }
              "
            ></ng-container>
          </ng-container>

          <!-- CHALLENGE REQUEST -->
          <ng-container *ngSwitchCase="item.type.startsWith('CHALLENGE_REQUEST') ? item.type : '__NONE__'">
            <ng-container
              *ngTemplateOutlet="
                contentBlock;
                context: {
                  title: 'NOTIFICATIONS.TEMPLATE.' + item.type + '.TITLE' | translate: getItemTranslate(item),
                  desc: 'CHALLENGES.' + item?.data?.type + '_TYPE_DESCRIPTION' | translate: getItemTranslate(item),
                  item: item,
                  showChallengeActions: item.data.status === 'PENDING' && !item.isRead,
                  isChallengeRequest: true
                }
              "
            ></ng-container>
          </ng-container>

          <!-- CHALLENGE RESULT -->
          <ng-container
            *ngSwitchCase="
              ['CHALLENGE_WON', 'CHALLENGE_LOST', 'CHALLENGE_DRAW'].includes(item.type) ? item.type : '__NONE__'
            "
          >
            <ng-container
              *ngTemplateOutlet="
                contentBlock;
                context: {
                  title: 'NOTIFICATIONS.TEMPLATE.' + item.type + '.TITLE' | translate: getItemTranslate(item),
                  desc: 'NOTIFICATIONS.TEMPLATE.' + item.type + '.DESCRIPTION' | translate: getItemTranslate(item),
                  item: item
                }
              "
            ></ng-container>
          </ng-container>

          <!-- CHALLENGE WAGER REQUEST -->
          <ng-container *ngSwitchCase="item.type.startsWith('CHALLENGE_WAGER_REQUEST') ? item.type : '__NONE__'">
            <ng-container
              *ngTemplateOutlet="wagerContentBlock; context: { item: item, isRequest: true }"
            ></ng-container>
          </ng-container>

          <!-- CHALLENGE WAGER RESULT -->
          <ng-container
            *ngSwitchCase="
              ['CHALLENGE_WAGER_WON', 'CHALLENGE_WAGER_LOST', 'CHALLENGE_WAGER_DRAW'].includes(item.type)
                ? item.type
                : '__NONE__'
            "
          >
            <ng-container
              *ngTemplateOutlet="wagerContentBlock; context: { item: item, isRequest: false }"
            ></ng-container>
          </ng-container>

          <!-- DEFAULT -->
          <ng-container *ngSwitchDefault>
            <ng-container
              *ngTemplateOutlet="
                contentBlock;
                context: {
                  title: item.title,
                  desc: item.description,
                  item: item
                }
              "
            ></ng-container>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- Load more -->
    <div class="d-flex justify-content-center py-3" *ngIf="nextToken">
      <button class="und__load-more-btn" type="button" (click)="loadMoreUserNotifications()">
        {{ 'BUTTON.LOAD_MORE' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- ── Shared content block ── -->
<ng-template
  #contentBlock
  let-title="title"
  let-desc="desc"
  let-item="item"
  let-showChallengeActions="showChallengeActions"
  let-isChallengeRequest="isChallengeRequest"
>
  <div class="d-flex align-items-center gap-1 mb-1">
    <div class="und__item-title flex-grow-1" [innerHTML]="title" [matTooltip]="title"></div>
    <span class="und__type-chip">{{ item.type | titlecase }}</span>
  </div>
  <div class="und__item-desc" [innerHTML]="desc" [matTooltip]="desc"></div>
  <div class="und__item-footer d-flex align-items-center justify-content-between mt-1">
    <span class="und__item-date">{{ getNotificationDate(item.id) | date: 'dd MMM yyyy' }}</span>
    <div class="d-flex align-items-center gap-1">
      <ng-container *ngIf="showChallengeActions">
        <button
          mat-icon-button
          [tabIndex]="-1"
          class="und__action-btn und__action-btn--accept"
          (click)="changeChallengeStatus(item, 'ACCEPTED')"
          [matTooltip]="'BUTTON.ACCEPT' | translate"
        >
          <mat-icon>check_circle_outline</mat-icon>
        </button>
        <button
          mat-icon-button
          [tabIndex]="-1"
          class="und__action-btn und__action-btn--reject"
          (click)="changeChallengeStatus(item, 'REJECTED')"
          [matTooltip]="'BUTTON.REJECT' | translate"
        >
          <mat-icon>block</mat-icon>
        </button>
      </ng-container>
      <button
        *ngIf="!showChallengeActions"
        mat-icon-button
        [tabIndex]="-1"
        class="und__action-btn und__action-btn--read"
        (click)="readUserNotification(item)"
        [disabled]="!!item.isRead"
        [matTooltip]="'NOTIFICATIONS.READ_BUTTON_TITLE' | translate"
      >
        <mat-icon>{{ item.isRead ? 'check' : 'check_circle' }}</mat-icon>
      </button>
    </div>
  </div>
</ng-template>

<!-- ── Wager content block ── -->
<ng-template #wagerContentBlock let-item="item" let-isRequest="isRequest">
  <div class="d-flex align-items-center gap-1 mb-1">
    <div
      class="und__item-title flex-grow-1"
      [innerHTML]="'NOTIFICATIONS.TEMPLATE.' + item.type + '.TITLE' | translate: getItemTranslate(item)"
      [matTooltip]="'NOTIFICATIONS.TEMPLATE.' + item.type + '.TITLE' | translate: getItemTranslate(item)"
    ></div>
    <span class="und__type-chip">Wager</span>
  </div>
  <div
    class="und__item-desc"
    [matTooltip]="'CHALLENGES.' + item?.data?.challenge?.type + '_TYPE_DESCRIPTION' | translate: getItemTranslate(item)"
  >
    {{ 'CHALLENGES.' + item?.data?.challenge?.type + '_TYPE_DESCRIPTION' | translate: getItemTranslate(item) }}
  </div>
  <div class="und__wager-row d-flex gap-2 mt-1">
    <div
      class="und__wager-side flex-grow-1"
      [class.und__wager-side--win]="item?.data?.initiatorId === authHelper.userProfile.cognitoId"
      [class.und__wager-side--loss]="item?.data?.opponentId === authHelper.userProfile.cognitoId"
    >
      <app-user-template
        class="width-100"
        [user]="item?.data?.initiatorVote"
        [displayTooltip]="false"
      ></app-user-template>
    </div>
    <div
      class="und__wager-side flex-grow-1"
      [class.und__wager-side--win]="item?.data?.opponentId === authHelper.userProfile.cognitoId"
      [class.und__wager-side--loss]="item?.data?.initiatorId === authHelper.userProfile.cognitoId"
    >
      <app-user-template
        class="width-100"
        [user]="item?.data?.opponentVote"
        [displayTooltip]="false"
      ></app-user-template>
    </div>
  </div>
  <div class="und__item-footer d-flex align-items-center justify-content-between mt-1">
    <span class="und__item-date">{{ getNotificationDate(item.id) | date: 'dd MMM yyyy' }}</span>
    <div class="d-flex align-items-center gap-1">
      <ng-container *ngIf="item.data.status === 'PENDING' && !item.isRead">
        <button
          mat-icon-button
          [tabIndex]="-1"
          class="und__action-btn und__action-btn--accept"
          (click)="changeChallengeWagerStatus(item, 'ACCEPTED')"
          [matTooltip]="'BUTTON.ACCEPT' | translate"
        >
          <mat-icon>check_circle_outline</mat-icon>
        </button>
        <button
          mat-icon-button
          [tabIndex]="-1"
          class="und__action-btn und__action-btn--reject"
          (click)="changeChallengeWagerStatus(item, 'REJECTED')"
          [matTooltip]="'BUTTON.REJECT' | translate"
        >
          <mat-icon>block</mat-icon>
        </button>
      </ng-container>
      <button
        *ngIf="!(item.data.status === 'PENDING' && !item.isRead)"
        mat-icon-button
        [tabIndex]="-1"
        class="und__action-btn und__action-btn--read"
        (click)="readUserNotification(item)"
        [disabled]="!!item.isRead"
        [matTooltip]="'NOTIFICATIONS.READ_BUTTON_TITLE' | translate"
      >
        <mat-icon>{{ item.isRead ? 'check' : 'check_circle' }}</mat-icon>
      </button>
    </div>
  </div>
</ng-template>
